import {
  Box,
  Button,
  Card,
  Chip,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import LocationIcon from "@mui/icons-material/LocationOnOutlined";
import BusinessIcon from "@mui/icons-material/BusinessOutlined";
import AllLocationsIcon from "@mui/icons-material/ArrowBackOutlined";
import { tokens } from "../theme";
import { useState } from "react";
import {
  Navigate,
  useLocation,
  useParams,
  useSearchParams,
  useNavigate,
} from "react-router-dom";
import { isCarePremise } from "@shared/types/foyer";
import { useAuth } from "../auth/AuthContext";
import { isCustomerSession } from "../auth/audience";
import { usePremiseWorkspace } from "../context/PremiseWorkspace";
import { PremiseScopeProvider } from "../context/PremiseScopeContext";
import { useScope } from "../context/ScopeContext";
import { productsForPremise } from "../nav/products";
import StreamingTvPage from "./StreamingTvPage";
import PremiseChannelsPage from "./PremiseChannelsPage";
import { PageContainer, PageHeader } from "../components/PageHeader";
import { PLACE_SECTIONS, sectionAccess } from "../components/RootLayout";
import { REFUSED_PATH, usePageAccess, usePermission } from "../auth/access";
import DevicesView from "../components/DevicesView";
import EnergyView from "../components/EnergyView";
import EngagementPanel from "../components/EngagementPanel";
import InstallationCodesView from "../components/InstallationCodesView";
import MediaGallery from "../components/MediaGallery";
import PairTVDialog from "../components/PairTVDialog";
import PhotoModerationQueue from "../components/PhotoModerationQueue";
import ResidentsEditor from "../components/ResidentsEditor";
import RoomsEditor from "../components/RoomsEditor";
import ServiceRequestQueue from "../components/ServiceRequestQueue";
import TemplateEditor from "../components/template/TemplateEditor";
import type { TemplateSubSection } from "../components/PremiseSidebar";
import StartViewPage from "./startnav/StartViewPage";
import InformationPage from "./startnav/InformationPage";
import AppearancePage, { APPEARANCE_KEYS } from "./startnav/AppearancePage";
import GuestCompanionPage from "./GuestCompanionPage";
import TranslationReview from "../components/ai/TranslationReview";
import OfferRevenuePage from "./OfferRevenuePage";
import OffersPage from "./OffersPage";
import RequestsPage from "./RequestsPage";
import HospitalityConfigPage from "./HospitalityConfigPage";
import PmsConfigurationPage from "./PmsConfigurationPage";
import PremiseOverviewPage from "./PremiseOverviewPage";
import PremiseProductsPage from "./PremiseProductsPage";
import SignagePlanContentPage from "./SignagePlanContentPage";
import SignageCampaignsPage from "./SignageCampaignsPage";
import SignageChannelsPage from "./SignageChannelsPage";
import SignageGroupsPage from "./SignageGroupsPage";
import SignageTemplatesPage from "./SignageTemplatesPage";
import SignageUpdatesPage from "./SignageUpdatesPage";
import SchedulePage from "./SchedulePage";
import VideoCallsPage from "./VideoCallsPage";
import { Trans, useTranslation } from "react-i18next";

/**
 * Sections that render their own `PageContainer` (and therefore their own
 * gutters). Every other panel is bare - it used to sit inside the premise
 * editor's padded content box, and needs that padding supplied here instead.
 */
const SELF_PADDED = new Set([
  "rooms",
  "media",
  "library",
  "residents",
  "products",
  // Offers renders its own PageContainer, at both layers. So does the staff
  // inbox beside it, and the revenue report beside that.
  "offers",
  "requests",
  "revenue",
  // The signage sections render their own PageContainer. `channels` is safe to
  // list even though Streaming TV also claims it - that branch falls through to
  // NotBuiltYet, which is self-padded too.
  "plan",
  "campaigns",
  "channels",
  // Start & navigation pages draw their own frame (breadcrumbs, columns).
  "home",
  "appearance",
]);

/**
 * Where an old `/frontlayer/start?tab=…` link lands now that the template
 * editor is split into Home screen and Appearance (Sigge, 2026-09-29). Kept
 * so a bookmark, a command-palette entry or a link in a message still opens
 * the thing it pointed at.
 */
export function legacyStartTarget(tab: string | null): string {
  if (!tab || tab === "picker") return "/frontlayer/appearance?item=picker";
  if (APPEARANCE_KEYS.has(tab)) return `/frontlayer/appearance?item=${tab}`;
  if (tab === "knobs") return "/frontlayer/home";
  if (tab === "slots") return "/promotions/slots";
  return `/frontlayer/home?item=${tab}`;
}

/**
 * Sections that render the Content library. Unlike every other section these
 * do NOT require a location: with a customer selected and no location, they
 * show that customer's shared library instead of asking you to pick one.
 */
const CONTENT_SECTIONS = new Set(["media", "library"]);

/**
 * Signage sections that mean something for a whole CUSTOMER, with no location
 * picked.
 *
 * This is what lets a chain hire a media manager: playlists, campaigns and day
 * plans authored here belong to the company layer, so all of its locations see
 * them (read-only) and can schedule them. Every one of these pages renders in
 * "chain mode" - a banner names the blast radius, because editing something
 * three hotels will play should never look like editing one.
 *
 * `plan` is included: the week grid's screen picker simply widens to every
 * screen the customer owns, grouped by location, which is the whole point of
 * being able to schedule across premises.
 */
const CHAIN_SECTIONS = new Set([
  "plan",
  "campaigns",
  "channels",
  // A chain designs one brand template for all its locations.
  "templates",
  // The sharpest case in this set: a screen group's whole reason to exist is
  // crossing locations, so it is MORE useful with no location picked than with
  // one. Company-layer groups can only be created from this scope.
  "groups",
]);

/*
 * ⚠️ This set and `CHAIN_CAPABLE_SECTIONS` in `nav/products.ts` have to agree.
 *
 * That one decides whether the sidebar OFFERS a section with no location
 * picked; this one decides whether the section RENDERS. Getting one without the
 * other produces the two worst outcomes available: an entry that is missing, or
 * an entry that leads to "these settings belong to one location". Groups
 * managed both on the way in (2026-09-01).
 *
 * They are not merged because they answer for different areas - this one is
 * Signage-only - but a section added to either should be checked against the
 * other, which is what `products.test.ts` is for.
 */

/**
 * Every section of every product area, rendered against the premise the header
 * is scoped to.
 *
 * One component rather than twenty route files on purpose: these are the exact
 * panels the premise editor used to switch between, with the same props. Only
 * where the premise comes from changed.
 */
const AREA_LANDING: Record<string, string> = {
  Location: "/places/overview",
  Frontlayer: "/frontlayer/home",
};

export default function WorkspacePage({
  area,
  section: fixedSection,
}: {
  area: string;
  /** For an area that is a single page (Guest web) rather than a list of
   *  sections in the URL. */
  section?: string;
}) {
  const { t } = useTranslation(["users", "common"]);
  const params = useParams<{ section: string; sub: string; leaf: string }>();
  const section = fixedSection ?? params.section ?? "";
  const subSection = params.sub;
  const leaf = params.leaf;
  const { premiseId, premise, loading, error, commit, applyServerChange } =
    usePremiseWorkspace();
  const { scope, companies, premises } = useScope();
  const { user } = useAuth();
  const customer = isCustomerSession(user);
  // The customer a no-location page works on. Nenda staff pick one in the
  // header; a customer never does - they ARE one - so theirs is their own
  // company. Without this every chain-wide page told a customer to "pick a
  // customer" they cannot pick (Sigge, 2026-09-28).
  const chainCompanyId =
    scope.companyId || (customer ? (user?.access?.homeCompanyId ?? "") : "");
  // What a page needing a customer shows when there is none. Staff are sent to
  // the header's Customer selector; a customer has no selector (they ARE one),
  // and one with no company - a premise admin - works one location at a time.
  const noCustomer = customer ? (
    <NoLocationPicked area={area} />
  ) : (
    <NoCustomerPicked />
  );
  // The customer that owns the media on this page: the scoped location's, or
  // the one selected on its own. Read from the directory rather than the premise config
  // so it is available before the premise config has loaded.
  const companyId =
    premises.find((p) => p.premiseId === premiseId)?.companyId ??
    (chainCompanyId || undefined);
  const companyName =
    companies.find((c) => c.id === (chainCompanyId || companyId))?.name ??
    t("workspacePage.thisCustomer");
  // Whether this place bought Signage - decides if a screen may be turned into
  // a signage display at all.
  const premiseProducts = productsForPremise(
    premises.find((p) => p.premiseId === premiseId)
  );
  const signageLicensed = premiseProducts.has("signage");
  const here = { premiseId: premiseId ?? "" };
  const canSeeResidents = usePermission("residents", "view", here);
  const canSeePhotos = usePermission("residents/photos", "view", here);
  const canHandleRequests = usePermission(
    "residents/care-requests",
    "view",
    here
  );
  const canViewPhi = usePermission("patient-data", "view", here);
  const mayOpen = usePageAccess();
  const location = useLocation();
  const [pairOpen, setPairOpen] = useState(false);
  const [codesTick, setCodesTick] = useState(0);
  // The template's open tab lives in the URL so the sidebar can link to it.
  const [searchParams, setSearchParams] = useSearchParams();
  const setTemplateSub = (sub: TemplateSubSection) =>
    setSearchParams(
      (prev) => {
        const q = new URLSearchParams(prev);
        if (sub === "picker") q.delete("tab");
        else q.set("tab", sub);
        return q;
      },
      { replace: true }
    );

  // Hidden in the menu, and the URL bar is a second way in.
  const mayOpenPath = (path: string) => {
    const [, , key] = path.split("/");
    const target = sectionAccess(area, key ?? "");
    return !target || mayOpen(target);
  };
  const access = sectionAccess(area, section);
  if (access && !mayOpen(access)) {
    const landing = AREA_LANDING[area];
    const landingOpens =
      landing && landing !== location.pathname && mayOpenPath(landing);
    const to = landingOpens ? landing : REFUSED_PATH;
    return <Navigate to={to} replace />;
  }
  // The template editor was split up; its old URL forwards to the new home.
  if (area === "Frontlayer" && section === "start") {
    return <Navigate to={legacyStartTarget(searchParams.get("tab"))} replace />;
  }
  // Start view's pages: Information's own sections and standard sections
  // (/frontlayer/home/info/sections|standard). Information itself is a
  // drawer on Start view, so /frontlayer/home/info opens it; any other
  // drill-down (an old /frontlayer/home/wifi) goes back to the table, which
  // forwards a screen filed under Information to the standard-sections page.
  if (
    area === "Frontlayer" &&
    section === "home" &&
    subSection &&
    !(subSection === "info" && (leaf === "sections" || leaf === "standard"))
  ) {
    const item =
      searchParams.get("item") ?? (subSection === "info" ? "info" : subSection);
    return <Navigate to={`/frontlayer/home?item=${item}`} replace />;
  }
  // Guest web is a menu area of its own now.
  if (area === "Frontlayer" && section === "guest-web") {
    return <Navigate to="/guest-web" replace />;
  }

  // Content is the one area that means something without a location: a
  // customer's shared library is edited at the customer level, by design.
  if (!premiseId && CONTENT_SECTIONS.has(section)) {
    if (!chainCompanyId) return noCustomer;
    return (
      <PremiseScopeProvider companyId={chainCompanyId}>
        <ContentLibrary companyName={companyName} />
      </PremiseScopeProvider>
    );
  }

  // Offers authored for a whole chain. Same page, company layer - the
  // no-location branch the media-layers note flags as the thing everyone
  // forgets, and the one POS-14 ("author once for the chain") builds on.
  if (!premiseId && section === "offers") {
    if (!chainCompanyId) return noCustomer;
    return (
      <PremiseScopeProvider companyId={chainCompanyId}>
        <OffersPage
          companyId={chainCompanyId}
          companyName={companyName}
          companyLocationCount={
            premises.filter((p) => p.companyId === chainCompanyId).length
          }
        />
      </PremiseScopeProvider>
    );
  }

  // The staff inbox for a whole chain: one queue per hotel, stacked and named.
  // A duty manager covering four properties should not have to re-scope the
  // header four times to find out whether anybody is waiting.
  if (!premiseId && section === "requests") {
    if (!chainCompanyId) return noCustomer;
    return (
      <PremiseScopeProvider companyId={chainCompanyId}>
        <RequestsPage
          companyName={companyName}
          chainPremises={premises
            .filter(
              (p) => p.companyId === chainCompanyId && p.segment !== "care"
            )
            .map((p) => ({
              premiseId: p.premiseId,
              premiseName: p.premiseName,
            }))}
        />
      </PremiseScopeProvider>
    );
  }

  // What every hotel of the customer earned, one row each, ranked. A group's
  // question rather than a hotel's, so the no-location branch is the primary
  // way this page is read rather than an afterthought (POS-12).
  if (!premiseId && section === "revenue") {
    if (!chainCompanyId) return noCustomer;
    return (
      <PremiseScopeProvider companyId={chainCompanyId}>
        <OfferRevenuePage
          companyId={chainCompanyId}
          companyName={companyName}
        />
      </PremiseScopeProvider>
    );
  }

  // Signage authored for the whole customer. Same pages, company layer, and a
  // banner so the reach is never a surprise.
  if (!premiseId && area === "Signage" && CHAIN_SECTIONS.has(section)) {
    if (!chainCompanyId) return noCustomer;
    const locationCount = premises.filter(
      (p) => p.companyId === chainCompanyId
    ).length;
    return (
      <PremiseScopeProvider companyId={chainCompanyId}>
        <ChainScopeBanner name={companyName} locationCount={locationCount} />
        {section === "plan" && (
          <SignagePlanContentPage companyId={chainCompanyId} />
        )}
        {section === "campaigns" && (
          <SignageCampaignsPage companyId={chainCompanyId} />
        )}
        {section === "channels" && (
          <SignageChannelsPage companyId={chainCompanyId} />
        )}
        {section === "groups" && (
          <SignageGroupsPage companyId={chainCompanyId} />
        )}
        {section === "templates" && (
          <SignageTemplatesPage companyId={chainCompanyId} />
        )}
      </PremiseScopeProvider>
    );
  }

  if (!premiseId) return <NoLocationPicked area={area} />;

  if (loading && !premise) {
    return (
      <PageContainer>
        <Stack spacing={2}>
          <Skeleton variant="text" width={240} height={40} />
          <Skeleton variant="rounded" height={120} />
          <Skeleton variant="rounded" height={240} />
        </Stack>
      </PageContainer>
    );
  }

  if (error || !premise) {
    return (
      <PageContainer>
        <Typography color="error">
          {error?.message ?? t("workspacePage.premiseNotFound")}
        </Typography>
      </PageContainer>
    );
  }

  const care = isCarePremise(premise);

  const body = (() => {
    switch (section) {
      /* ---- Places & Screens ------------------------------------------- */
      case "overview":
        return <PremiseOverviewPage premise={premise} onCommit={commit} />;
      case "products":
        return <PremiseProductsPage premise={premise} />;
      /**
       * Ruled by Sigge 2026-09-07: Installation code and PMS stopped being
       * their own top-level destinations and became sub-tabs here instead -
       * `codes`/`pms` still resolve (an old bookmark or command-palette
       * entry pointing at either is a link somebody made, same reasoning as
       * the `carousels`/`pages` alias below), landing on this SAME case
       * with that sub-tab pre-selected.
       */
      case "settings":
      case "codes":
      case "pms": {
        const settingsTab = (searchParams.get("tab") ??
          (section === "codes" || section === "pms" ? section : "general")) as
          | "general"
          | "codes"
          | "pms";
        return (
          <Stack spacing={2}>
            <Tabs
              value={settingsTab}
              onChange={(_e, next: string) =>
                setSearchParams(
                  (prev) => {
                    const q = new URLSearchParams(prev);
                    if (next === "general") q.delete("tab");
                    else q.set("tab", next);
                    return q;
                  },
                  { replace: true }
                )
              }
              data-testid="settings-sub-tabs"
            >
              <Tab
                value="general"
                label={t("workspacePage.settingsTabs.general")}
                data-testid="settings-sub-tab-general"
              />
              <Tab
                value="codes"
                label={t("workspacePage.settingsTabs.installationCode")}
                data-testid="settings-sub-tab-codes"
              />
              {/* PMS is a hotel concern - a care premise has no folio to post
                  to. Same gate `PLACE_SECTIONS`'s old `pms` entry used
                  (`only: "hotel"`). */}
              {!care && (
                <Tab
                  value="pms"
                  label={t("workspacePage.settingsTabs.pms")}
                  data-testid="settings-sub-tab-pms"
                />
              )}
            </Tabs>
            {settingsTab === "general" && (
              <HospitalityConfigPage premise={premise} onCommit={commit} />
            )}
            {settingsTab === "codes" && (
              <InstallationCodesView
                premiseId={premise.premiseId}
                onCreateClick={() => setPairOpen(true)}
                refreshTick={codesTick}
              />
            )}
            {settingsTab === "pms" && !care && (
              <PmsConfigurationPage premiseId={premise.premiseId} />
            )}
          </Stack>
        );
      }
      case "rooms":
        return (
          <PageContainer>
            <PageHeader title={t("workspacePage.unitsTitle")} />
            <RoomsEditor
              rooms={premise.rooms}
              onCommit={commit}
              onServerChange={applyServerChange}
              premiseId={premise.premiseId}
              segment={premise.segment}
              premiseProducts={premiseProducts}
              staff={!customer}
            />
          </PageContainer>
        );
      case "devices":
        return (
          <DevicesView
            premiseId={premise.premiseId}
            allowDoorSign={care}
            // Offered only where Signage was actually bought - a role that
            // turns a screen into something the customer has no licence for
            // is a switch that cannot work.
            allowSignage={signageLicensed}
            occupancyDerived={care}
            showGuestCasting={!care}
            rooms={premise.rooms}
          />
        );

      /* ---- Guest TV ---------------------------------------------------- */
      case "home":
        if (
          subSection === "info" &&
          (leaf === "sections" || leaf === "standard")
        ) {
          return (
            <InformationPage
              key={leaf}
              mode={leaf === "sections" ? "own" : "standard"}
              premise={premise}
              onCommit={commit}
            />
          );
        }
        return <StartViewPage premise={premise} onCommit={commit} />;
      case "appearance":
        return <AppearancePage premise={premise} onCommit={commit} />;
      case "schedule":
        return <SchedulePage premise={premise} onCommit={commit} />;
      // The home-screen placement. It edits the TEMPLATE (a slot is declared by
      // the manifest and filled per premise), so it reuses the template editor
      // pinned to that one tab rather than growing a second editor for the same
      // data - but it lives under Promotions, beside the catalogue it places.
      case "slots":
        return (
          <TemplateEditor
            premise={premise}
            sub="slots"
            onCommit={commit}
            onSelectSub={setTemplateSub}
          />
        );
      case "offers":
        return <OffersPage premise={premise} />;
      case "requests":
        return <RequestsPage premise={premise} />;
      case "revenue":
        return <OfferRevenuePage premise={premise} />;
      case "guest-web":
        return <GuestCompanionPage premise={premise} onCommit={commit} />;
      case "translate":
        return (
          <PageContainer>
            <PageHeader
              eyebrow={t("workspacePage.translate.eyebrow")}
              title={t("workspacePage.translate.title")}
              subtitle={t("workspacePage.translate.subtitle")}
            />
            <TranslationReview premise={premise} onCommit={commit} />
          </PageContainer>
        );
      // Frontlayer calls it "media", Signage calls it "library" - one panel
      // serves both, because it is the same library either way.
      case "media":
      case "library":
        return <ContentLibrary premiseName={premise.premiseName} />;

      /* ---- Signage ----------------------------------------------------- */
      // "channels" is claimed by Streaming TV as well; the area decides which
      // one you get, so a signage-only place never lands on the TV line-up.
      case "plan":
        return <SignagePlanContentPage premiseId={premise.premiseId} />;
      case "campaigns":
        return <SignageCampaignsPage premiseId={premise.premiseId} />;
      case "channels":
        // Two areas, two different questions. Signage's Channels is a playlist
        // surface; Streaming TV's is the ORDER a guest's channel list comes out
        // in — the national line-up, and this hotel departing from it.
        return area === "Signage" ? (
          <SignageChannelsPage premiseId={premise.premiseId} />
        ) : (
          <PremiseChannelsPage premiseId={premise.premiseId} />
        );
      case "groups":
        return <SignageGroupsPage premiseId={premise.premiseId} />;
      case "templates":
        return <SignageTemplatesPage premiseId={premise.premiseId} />;
      case "updates":
        return <SignageUpdatesPage premiseId={premise.premiseId} />;

      /* ---- Streaming TV ------------------------------------------------ */
      // The home page's rails. Under Streaming TV rather than on the premise's
      // own pages: without that product there is nothing for a carousel to
      // appear on, and the nav group is already gated on holding it.
      /* ---- Streaming TV: one screen, one menu item -------------------- */
      case "menu":
        return <StreamingTvPage premiseId={premise.premiseId} />;
      // The old split. Kept as redirects rather than 404s: a bookmark or a
      // command-palette entry pointing at either is a link somebody made.
      case "carousels":
      case "pages":
        return <StreamingTvPage premiseId={premise.premiseId} />;

      /* ---- Care -------------------------------------------------------- */
      case "residents":
        return (
          <PageContainer>
            <Stack spacing={5}>
              {canSeeResidents && (
                <ResidentsEditor
                  premiseId={premise.premiseId}
                  rooms={premise.rooms}
                  identityMode={premise.care?.doorSignIdentityMode ?? "none"}
                  // Both are already stored by their own endpoints: mirror them
                  // into the saved config so this copy matches what is stored.
                  onIdentityModeChange={(mode) =>
                    applyServerChange((c) => ({
                      ...c,
                      care: { ...c.care, doorSignIdentityMode: mode },
                    }))
                  }
                  onRoomOccupancyChange={(roomId, occupied) =>
                    applyServerChange((c) => ({
                      ...c,
                      rooms: c.rooms.map((r) =>
                        r.id === roomId ? { ...r, occupied } : r
                      ),
                    }))
                  }
                  canViewPhi={canViewPhi}
                  premiseTimezone={premise.address?.timezone}
                />
              )}
              {canHandleRequests && (
                <ServiceRequestQueue
                  premiseId={premise.premiseId}
                  rooms={premise.rooms}
                />
              )}
              {canSeePhotos && (
                <PhotoModerationQueue
                  premiseId={premise.premiseId}
                  rooms={premise.rooms}
                />
              )}
            </Stack>
          </PageContainer>
        );
      case "video-calls":
        return <VideoCallsPage premise={premise} />;

      /* ---- Analytics --------------------------------------------------- */
      case "energy":
        return <EnergyView premiseId={premise.premiseId} />;
      case "engagement":
        return (
          <EngagementPanel premiseId={premise.premiseId} showCasting={!care} />
        );

      default:
        return <NotBuiltYet area={area} section={section} />;
    }
  })();

  return (
    <PremiseScopeProvider premiseId={premise.premiseId} companyId={companyId}>
      {/* Ruled by Sigge 2026-09-07: the seven-item "Places & Screens"
          sidebar group is now one link (see RootLayout.tsx); this is where
          its five sections actually live once you're inside one. */}
      {area === "Location" && <PlaceTopTabs section={section} />}
      {SELF_PADDED.has(section) ? (
        body
      ) : (
        <Box sx={{ p: { xs: 2, md: 4 } }}>{body}</Box>
      )}
      <PairTVDialog
        open={pairOpen}
        premiseId={premise.premiseId}
        premiseName={premise.premiseName}
        onClose={() => setPairOpen(false)}
        onCreated={() => setCodesTick((t) => t + 1)}
      />
    </PremiseScopeProvider>
  );
}

/**
 * The Content library, at whichever layer the header is scoped to.
 *
 * With a location selected it shows that location's files plus the ones its
 * customer shares across every location it owns - the inherited ones carry a
 * "Company" chip and cannot be edited here. With only a customer selected it
 * IS that shared library, and uploads land there for every location at once.
 *
 * The Global media library is not merged in; it lives on its own admin page
 * and is reachable from the image picker. See `AssetsPicker`.
 */
function ContentLibrary({
  premiseName,
  companyName,
}: {
  premiseName?: string;
  companyName?: string;
}) {
  const { t } = useTranslation("users");
  const atCompany = !premiseName;
  return (
    <PageContainer>
      <PageHeader
        eyebrow={
          atCompany
            ? t("workspacePage.contentLibrary.eyebrowCustomer")
            : t("workspacePage.contentLibrary.eyebrowLocation")
        }
        title={t("workspacePage.contentLibrary.title")}
        subtitle={
          atCompany
            ? t("workspacePage.contentLibrary.subtitleCompany", {
                company: companyName,
              })
            : t("workspacePage.contentLibrary.subtitleLocation", {
                premise: premiseName,
              })
        }
      />
      <MediaGallery />
    </PageContainer>
  );
}

/** Content at the customer layer needs a customer, the way every other product
 *  section needs a location. Same shape of message, different selector. */
/**
 * Names the blast radius of a chain-level edit.
 *
 * Signage authored for a customer plays in every location it owns, and the page
 * underneath is pixel-identical to the single-location one. Without something
 * saying so, an operator has no way to tell which of the two they are looking
 * at - and the mistake is only visible on a wall, in another building.
 */
function ChainScopeBanner({
  name,
  locationCount,
}: {
  name: string;
  locationCount: number;
}) {
  const { t } = useTranslation("users");
  return (
    <Box sx={{ px: { xs: 2, md: 4 }, pt: { xs: 2, md: 4 } }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 2,
          py: 1.25,
          borderRadius: "8px",
          border: `1px solid ${tokens.brand}`,
          bgcolor: tokens.brandTintSoft,
        }}
      >
        <BusinessIcon sx={{ fontSize: 20, color: tokens.brand }} />
        <Typography variant="body2" sx={{ color: tokens.ink }}>
          <Trans
            t={t}
            i18nKey="workspacePage.chainBanner.text"
            count={locationCount}
            values={{ name }}
            components={{ strong: <strong /> }}
          />
        </Typography>
      </Box>
    </Box>
  );
}

function NoCustomerPicked() {
  const { t } = useTranslation("users");
  return (
    <PageContainer>
      <PageHeader
        eyebrow={t("workspacePage.noCustomer.eyebrow")}
        title={t("workspacePage.noCustomer.title")}
      />
      <Card variant="outlined" sx={{ p: 4 }}>
        <Stack spacing={1.5} alignItems="flex-start">
          <Typography variant="body2" color="text.secondary">
            <Trans
              t={t}
              i18nKey="workspacePage.noCustomer.body"
              components={{ strong: <strong /> }}
            />
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() =>
              document
                .querySelector<HTMLElement>('[data-testid="scope-customer"]')
                ?.click()
            }
          >
            {t("workspacePage.noCustomer.button")}
          </Button>
        </Stack>
      </Card>
    </PageContainer>
  );
}

/**
 * Dashboard / Screens / Settings / Products / Rooms - a real top tab bar,
 * ruled by Sigge 2026-09-07 to replace the seven-item nested sidebar group
 * this used to be (`RootLayout.tsx`'s old "Places & Screens" children). The
 * FIRST such bar in the CMS - every other section still navigates through
 * the left sidebar - so it reads `PLACE_SECTIONS` rather than a second,
 * easily-drifting copy of the same five labels.
 *
 * `codes` and `pms` are legacy route segments, not tabs of their own any
 * more (both fold into `settings` - see the switch in `WorkspacePage`), so
 * either one highlights the Settings tab rather than leaving nothing
 * selected.
 */
function PlaceTopTabs({ section }: { section: string }) {
  const { t } = useTranslation(["shell", "users"]);
  const navigate = useNavigate();
  const activeKey =
    section === "codes" || section === "pms" ? "settings" : section;
  const mayOpen = usePageAccess();
  const sections = PLACE_SECTIONS.filter((s) => mayOpen(s.access));
  // Not a Places & Screens section at all (shouldn't happen - `area` already
  // gated this) - nothing to show rather than a Tabs with no value matching
  // any Tab, which MUI logs a warning about.
  // Every premise this session can see, not just the header's customer: the
  // way back to the list only makes sense when there is a list to go back to.
  const { premises } = useScope();
  if (!sections.some((s) => s.key === activeKey)) return null;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        px: { xs: 2, md: 4 },
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      {premises.length > 1 && (
        // The customer's route back to their premise list. An admin has the
        // Companies page for this; a customer does not, and without this the
        // list was reachable only by clearing the header's Location picker.
        <Button
          size="small"
          startIcon={<AllLocationsIcon />}
          onClick={() => navigate("/premises")}
          sx={{ flexShrink: 0, textTransform: "none" }}
          data-testid="place-top-tabs-all-locations"
        >
          {t("users:workspacePage.allLocations")}
        </Button>
      )}
      <Tabs
        value={activeKey}
        onChange={(_e, next: string) => navigate(`/places/${next}`)}
        sx={{ minWidth: 0 }}
        variant="scrollable"
        scrollButtons="auto"
        data-testid="place-top-tabs"
      >
        {sections.map((s) => (
          <Tab
            key={s.key}
            value={s.key}
            label={t(s.label)}
            // `AreaSection.icon` is typed as the broader `ReactNode` (it also
            // has to satisfy plain nav items elsewhere); every entry is
            // actually a real element, which is all MUI's stricter `icon` prop
            // accepts.
            icon={s.icon as React.ReactElement}
            iconPosition="start"
            sx={{ minHeight: 48 }}
            data-testid={`place-top-tab-${s.key}`}
          />
        ))}
      </Tabs>
    </Box>
  );
}

/**
 * A section that exists in the menu but has no editor behind it yet.
 *
 * Shown rather than hidden: the product WAS bought, and a licence you cannot
 * see anywhere reads as a switch that did not work.
 */
function NotBuiltYet({ area, section }: { area: string; section: string }) {
  const { t } = useTranslation("users");
  const pretty = section.replace(/-/g, " ");
  return (
    <PageContainer>
      <PageHeader
        eyebrow={area}
        title={pretty.replace(/^./, (c) => c.toUpperCase())}
      />
      <Card variant="outlined" sx={{ p: 4 }}>
        <Stack spacing={1} alignItems="flex-start">
          <Chip
            size="small"
            color="warning"
            variant="outlined"
            label={t("workspacePage.notBuilt.chip")}
          />
          <Typography variant="body2" color="text.secondary">
            <Trans
              t={t}
              i18nKey="workspacePage.notBuilt.licensed"
              values={{ area }}
              components={{ strong: <strong /> }}
            />
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("workspacePage.notBuilt.provisioning")}
          </Typography>
        </Stack>
      </Card>
    </PageContainer>
  );
}

/** Every product page needs a location. Say so, and say where to set it. */
function NoLocationPicked({ area }: { area: string }) {
  const { t } = useTranslation("users");
  return (
    <PageContainer>
      <PageHeader eyebrow={area} title={t("workspacePage.noLocation.title")} />
      <Card variant="outlined" sx={{ p: 4 }}>
        <Stack spacing={1.5} alignItems="flex-start">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: "text.secondary",
            }}
          >
            <LocationIcon fontSize="small" />
            <Typography variant="body2">
              {t("workspacePage.noLocation.lead")}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            <Trans
              t={t}
              i18nKey="workspacePage.noLocation.body"
              components={{ strong: <strong /> }}
            />
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() =>
              document
                .querySelector<HTMLElement>('[data-testid="scope-location"]')
                ?.click()
            }
          >
            {t("workspacePage.noLocation.button")}
          </Button>
        </Stack>
      </Card>
    </PageContainer>
  );
}
